import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Activity, BarChart3, LogOut, TrendingUp, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { adminClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { AdminLogin } from "@/pages/AdminPage";
import PlayersTab from "@/components/datavolley/PlayersTab";
import MatchesTab from "@/components/datavolley/MatchesTab";
import LiveBoard from "@/components/datavolley/LiveBoard";
import StatsView from "@/components/datavolley/StatsView";
import SeasonView from "@/components/datavolley/SeasonView";
import PublicView from "@/components/datavolley/PublicView";
import LangToggle from "@/components/datavolley/LangToggle";
import { loadCachedEvents, loadQueue, mergeEvents } from "@/lib/dvStore";

import { useSeo } from "@/lib/seo";
export default function DataVolleyPage() {
  useSeo({ title: "Ν.Α.Ο. Σούδας — Volleyball Stats", description: "Match statistics and season progress for Ν.Α.Ο. Σούδας volleyball, powered by The Sand Lab in Chania, Greece.", path: "/naos" });
  const [token, setToken] = useState(localStorage.getItem("sandlab_admin_token"));
  const [showLogin, setShowLogin] = useState(false);
  if (!token && !showLogin) return <PublicView onLogin={() => setShowLogin(true)} />;
  if (!token) return <AdminLogin onLogin={(tk) => setToken(tk)} subtitle="DataVolley · Coach access" />;
  return (
    <DataVolleyShell
      onLogout={() => {
        localStorage.removeItem("sandlab_admin_token");
        localStorage.removeItem("sandlab_admin_username");
        setToken(null);
      }}
    />
  );
}

function DataVolleyShell({ onLogout }) {
  const { t } = useT();
  const [view, setView] = useState(() => (localStorage.getItem("dv_open_match") ? "live" : "matches"));
  const [match, setMatch] = useState(null);

  useEffect(() => {
    const id = localStorage.getItem("dv_open_match");
    if (!id) return;
    adminClient
      .get(`/datavolley/matches/${id}`)
      .then(({ data }) => setMatch(data.match))
      .catch(() => {
        localStorage.removeItem("dv_open_match");
        setView("matches");
      });
  }, []);

  const openMatch = (m, v) => {
    setMatch(m);
    localStorage.setItem("dv_open_match", m.id);
    setView(v);
  };
  const back = () => {
    localStorage.removeItem("dv_open_match");
    setMatch(null);
    setView("matches");
  };

  const inMatch = (view === "live" || view === "stats") && match;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="datavolley-page">
      <header className="sticky top-0 z-40 bg-slate-950 text-white">
        <div className="px-3 sm:px-5 h-12 sm:h-14 flex items-center gap-2 sm:gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="dv-home-link">
            <img src="/club-logo-white.png" alt="Ν.Α.Ο. Σούδας" className="h-10 w-auto" />
          </Link>
          <span className="font-heading font-black text-lg tracking-tight hidden sm:inline">
            Data<span className="text-sunset">Volley</span>
          </span>
          <nav className="ml-2 flex items-center gap-1 rounded-xl bg-slate-800/70 p-1">
            {inMatch ? (
              <>
                <button onClick={back} data-testid="dv-back-btn" className="dv-btn h-9 px-3 rounded-lg text-sm font-semibold text-slate-300 hover:text-white flex items-center gap-1.5">
                  <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{t("dv_back")}</span>
                </button>
                <NavPill active={view === "live"} onClick={() => setView("live")} icon={Activity} label={t("dv_live")} testid="dv-nav-live" />
                <NavPill active={view === "stats"} onClick={() => setView("stats")} icon={BarChart3} label={t("dv_stats")} testid="dv-nav-stats" />
              </>
            ) : (
              <>
                <NavPill active={view === "matches"} onClick={() => setView("matches")} icon={Trophy} label={t("dv_matches")} testid="dv-nav-matches" />
                <NavPill active={view === "players"} onClick={() => setView("players")} icon={Users} label={t("dv_players")} testid="dv-nav-players" />
                <NavPill active={view === "season"} onClick={() => setView("season")} icon={TrendingUp} label={t("dv_season")} testid="dv-nav-season" />
              </>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <LangToggle />
            <button onClick={onLogout} data-testid="dv-logout-btn" className="dv-btn h-9 px-3 rounded-lg text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800 flex items-center gap-1.5">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("dv_logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className={inMatch ? "px-2 sm:px-3 py-3" : "max-w-[1500px] mx-auto px-3 sm:px-5 py-4"}>
        {view === "matches" && <MatchesTab onOpenLive={(m) => openMatch(m, "live")} onOpenStats={(m) => openMatch(m, "stats")} />}
        {view === "players" && <PlayersTab />}
        {view === "season" && <SeasonView />}
        {view === "live" && match && <LiveBoard key={match.id} match={match} onViewStats={() => setView("stats")} onMatchUpdate={setMatch} />}
        {view === "stats" && match && <MatchStats match={match} />}
        {(view === "live" || view === "stats") && !match && <p className="text-center text-slate-500 py-10">…</p>}
      </main>
    </div>
  );
}

function NavPill({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`dv-btn h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
        active ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function MatchStats({ match }) {
  const [events, setEvents] = useState(() => mergeEvents(loadCachedEvents(match.id), loadQueue(match.id)));
  useEffect(() => {
    adminClient
      .get(`/datavolley/matches/${match.id}`)
      .then(({ data }) => setEvents(mergeEvents(data.events, loadQueue(match.id))))
      .catch(() => toast.message("Offline · showing local data"));
  }, [match.id]);
  return <StatsView match={match} events={events} />;
}
